import { useEffect, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import noReviewsFoundImg from 'assets/noReviewsFound.png';

import CreateReview from 'Pages/TeamDashboard/CreateReview';

import { setCurrentRecord } from 'Redux/Actions/historyActions';

import { ROUTES } from 'routes/route.constants';

import { getSharedDesignReviewByToken } from 'services/teamDashboardReviews';

import styles from './styles.module.scss';

const ShareDesignReview = () => {
  const { token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  useEffect(() => {
    const MAX_RETRIES = 2;

    const isApiFail = err => {
      const responseStatus = err?.response?.status;

      // Retry only on network errors (no response) or server errors (5xx)
      return !responseStatus || responseStatus >= 500;
    };

    const fetchWithRetry = async (attempt = 0) => {
      try {
        const data = await getSharedDesignReviewByToken(token);

        // Workspace members get redirected to the full member view instead of the public/view-only page
        if (data?.requesting_user_workspace_access?.is_member && data?.uuid) {
          navigate(ROUTES.TEAM_REVIEWS_DETAIL(data.uuid), { replace: true });

          return;
        }

        setReviewData(data);
        dispatch(setCurrentRecord(data));
      }
      catch (err) {
        const responseStatus = err?.response?.status;
        const body = err?.response?.data || {};
        const code = body?.code;

        if (responseStatus === 401) {
          navigate(ROUTES.SHARE_DESIGN_REVIEW_AUTH(token), {
            state: { shareContext: body?.share_context },
          });

          return;
        }

        if (isApiFail(err) && attempt < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
          await fetchWithRetry(attempt + 1);

          return;
        }

        if (responseStatus === 403 && code === 'FORBIDDEN_DOMAIN') {
          setError(
            body?.detail || 'You don\'t have access to this product review.'
          );
          setErrorCode('FORBIDDEN_DOMAIN');
        }
        else {
          setError(
            err?.message || 'This share link is invalid or has been removed.'
          );
          setErrorCode(null);
        }
      }
    };

    const fetchDesignReview = async () => {
      if (!token) {
        setError('Invalid share link');
        setErrorCode(null);
        setLoading(false);

        return;
      }

      setLoading(true);
      setError(null);
      setErrorCode(null);

      await fetchWithRetry();
      setLoading(false);
    };

    fetchDesignReview();
  }, [token, dispatch, navigate]);

  // Cleanup: Clear current record when component unmounts
  useEffect(() => () => {
    dispatch(setCurrentRecord(null));
  }, [dispatch]);

  const handleClose = () => {
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className={styles['loader-container']}>
        <ImpulseSpinner frontColor={'#28BDAE'} />
      </div>
    );
  }

  if (error && errorCode === 'FORBIDDEN_DOMAIN') {
    return (
      <div className={styles['error-page']}>
        <div className={styles['error-card']}>
          <img
            src={noReviewsFoundImg}
            alt="Access denied"
            className={styles['error-illustration']}
          />
          <h1 className={styles['error-title']}>
            Access Denied
          </h1>
          <p className={styles['error-subtitle']}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['error-page']}>
        <div className={styles['error-card']}>
          <img
            src={noReviewsFoundImg}
            alt="Design audit not found"
            className={styles['error-illustration']}
          />
          <h1 className={styles['error-title']}>
            Sorry, we couldn't load your audit
          </h1>
          <p className={styles['error-subtitle']}>
            The audit you're trying to view couldn't load for unknown reasons
          </p>
          <div className={styles['troubleshoot-box']}>
            <p className={styles['troubleshoot-label']}>
              A few things that might help:
            </p>
            <ul className={styles['troubleshoot-list']}>
              <li>
                Try switching to a different Wifi network or mobile hotspot
              </li>
              <li>
                Open in an incognito window
              </li>
              <li>
                Hard refresh the page by using
                <span className={styles.kbd}>
                  Cmd+Shift+R
                </span>
                on Mac or
                <span className={styles.kbd}>
                  Ctrl+Shift+R
                </span>
                on Windows
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (reviewData) {
    return (
      <CreateReview
        onClose={handleClose}
        existingReviewData={reviewData}
        isPublicView
      />
    );
  }

  return null;
};

ShareDesignReview.propTypes = {};

export default ShareDesignReview;
